import React, { useEffect, useState, useRef, useLayoutEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  braintreePurchase,
  sendMainPurchaseRequest,
  selectFormValidity,
  selectID,
  selectPurchaseEventId,
  selectShowPaymentError,
  selectSuccess,
  selectIfCardFormShown,
  setCardholderName,
  setIfCardFormShown,
  setShowPaymentError,
  selectShowSuccessPopup,
  setShowSuccessPopup,
} from "../../store/checkout";
import {
  selectAnalyticsData,
  selectEventsData,
  setEventDataList,
} from "../../store/events";
import RedditPixel from "react-reddit-pixel";
import {
  selectBtToken,
  selectEmail,
  selectToken,
  selectPaymentProvider,
  selectStripeAccountName,
} from "../../store/signup";
import {
  selectDefaultPlanId,
  selectPlan,
  selectPlanCoupon,
  selectShowCheckout,
  setPlan,
  setShowCheckout,
} from "../../store/plans";
import { PLANS } from "../../analytics/plans";
import { selectLandingType } from "../../store/plans";
import { useNavigate } from "react-router-dom";
import { setCheckoutLoader } from "../../store/loader";
import { EVENTS } from "../../analytics/events";
import {
  sendPurchaseEvent,
  sendAnalyticsEvents,
  sendRunwayerConversion,
} from "../../analytics/common";
import { AppButton } from "../../components";
import styled from "styled-components";
import { Elements, PaymentRequestButtonElement } from "@stripe/react-stripe-js";
import braintree from "braintree-web";
import Stripe from "./Stripe";
import Braintree from './Braintree';
import PaypalButton from './components/PaypalButton';
import HybridPlan from "../plans/components/HybridPlan";
import { MoneyBackDescription, MoneyBackIcon, MoneyBackTitle, MoneyBackWrapper } from "../plans/SpecialPlans";
import { setAmplitudeUserProperties } from '../../analytics/amplitude';
import { SuccessPopup } from "./components/SuccessPopup";

const isStage = process.env.REACT_APP_ENV === "stage";
const textAlign = navigator.language.startsWith('ar') ? 'right' : 'left';

const ArrowLeftIcon = (
  <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7 14C6.708 14 6.418 13.873 6.22 13.627L0.22066 7.62698C-0.07734 7.25598 -0.07334 6.72598 0.23166 6.35998L6.23166 0.359984C6.58466 -0.0640163 7.21566 -0.121016 7.64066 0.231984C8.06466 0.584984 8.12166 1.21598 7.76766 1.63998L2.29266 7.01098L7.779 12.373C8.125 12.803 8.057 13.433 7.626 13.779C7.442 13.928 7.22 14 7 14Z"
      fill="#323232"
    />
  </svg>
);

const CheckoutWrapper = styled.div`
  max-width: 330px;
`;

const CheckoutHeadWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  width: 100%;
  position: relative;
  margin-top: 64px;
`;

const StyledBackButton = styled.button`
  border: none;
  background-color: transparent;
  padding: 4px;
  position: absolute;
  left: 5px;
  top: 3px;
  cursor: pointer;
`;

const Title = styled.div`
  font-weight: ${(props) => props.theme.fontWeights.extraBold};
  font-size: ${(props) => props.theme.fontSizes.font28};
  line-height: ${(props) => props.theme.lineHeights.lineHeight24};
  color: ${(props) => props.theme.colors.colorText};
`;

const PaymentRequestContainer = styled.div`
  width: 100%;
  max-width: 330px;
  margin-bottom: ${({ $mt }) =>
    $mt ? '0px' : '24px'};
  margin-top: ${({ $mt }) =>
    $mt ? `${+$mt}${typeof $mt === 'number' ? 'px' : ''}` : '0px'};
  div iframe {
    border-radius: 12px;
  }
`;

export const CardIconsList = styled.ul`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 16px;
  img {
    &:not(:last-child) {
      margin-right: 15px;
    }
  }
`;

const AlternativePaymentButtons = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const MoneyBack = styled.div`
  margin-top: 16px;
  display: inline-block;
  padding-left: 22px;
  font-weight: ${(props) => props.theme.fontWeights.extraLight};
  font-size: ${(props) => props.theme.fontSizes.font14};
  line-height: ${(props) => props.theme.lineHeights.lineHeight24};
  color: ${(props) => props.theme.colors.colorCheckoutOr};
  position: relative;
  &::before {
    content: url(${'../assets/checkout/money-back.svg'});
    width: 15px;
    height: 16px;
    position: absolute;
    left: -2px;
    top: 0;
  }
`;

export const StyledBtnWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 7px;
`;

export const StyledBtn = styled.button`
  background-color: transparent;
  letter-spacing: -0.128px;
  text-decoration: underline;
  font-weight: ${(props) => props.theme.fontWeights.extraLight};
  font-size: ${(props) => props.theme.fontSizes.font12};
  line-height: ${(props) => props.theme.lineHeights.lineHeight24};
  color: ${(props) => props.theme.colors.colorCheckoutOr};
  display: flex;
  align-items: center;
  cursor: pointer;
  border: none;
  &:first-child {
    margin-right: 20px;
  }
  &:focus,
  &:active {
    outline: none;
  }
  svg {
    &:hover {
      opacity: 0.8;
    }
  }
`;

const PayWithContainer = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
  max-width: 330px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PayWithLine = styled.div`
  max-width: 140px;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.colorInactivePlanBorder};
`;

const PayWithText = styled.div`
  font-weight: ${(props) => props.theme.fontWeights.extraLight};
  font-size: ${(props) => props.theme.fontSizes.font18};
  line-height: ${(props) => props.theme.lineHeights.lineHeight24};
  color: ${(props) => props.theme.colors.colorCheckoutOr};
  text-align: center;
  margin: 0 12px;
`;

export const CARD_BRANDS = [
  { name: "visa", width: "70px", height: "21px" },
  { name: "mastercard", width: "45px", height: "28px" },
  { name: "amex", width: "51px", height: "24px" },
  { name: "discover", width: "49px", height: "10px" },
];

const style = {
  base: {
    color: '#3393EC',
    fontSize: '18px',
    lineHeight: '24px',
    fontWeight: 300,
    fontFamily: 'Open Sans, Segoe UI, sans-serif',
    textAlign,

    '::placeholder': {
      color: '#ADADAD',
      letterSpacing: '1.2px',
      fontSize: '18px',
      lineHeight: '24px',
    },
  },
};

const CheckoutScreen = ({ stripePromise }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const landingType = useSelector(selectLandingType);
  const isRedirectToUpsell = [
    'alfa',
    'alfa2',
    'alfa_bravo',
    'trial_v2',
    'trial_v2eur',
    'trivia',
  ].includes(landingType);
  const isHybridPlans = ['trial_exp', 'trial_chp'].includes(landingType);
  const isRunwayerFlow = ['alfa_bravo'].includes(landingType);
  const showError = useSelector(selectShowPaymentError);
  const isFormValid = useSelector(selectFormValidity);
  const eventsData = useSelector(selectEventsData);
  const isSuccess = useSelector(selectSuccess);
  const subscriptionId = useSelector(selectID);
  const purchaseEventId = useSelector(selectPurchaseEventId);
  const [planDetails, setPlanDetails] = useState(null);
  const token = useSelector(selectToken);
  const name = useSelector(selectEmail);
  const plan = useSelector(selectPlan);
  const [load, setLoad] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const stripeInstance = useRef();
  const card = useRef();
  const expiration = useRef();
  const cvc = useRef();
  const cardholder = useRef(null);
  const braintreeInstance = useRef();
  const braintreeCardInstance = useRef();
  const braintreeThreeDSInstance = useRef();
  const paypal = useRef();
  const analyticsParams = useSelector(selectAnalyticsData);
  const btToken = useSelector(selectBtToken);
  const showCheckout = useSelector(selectShowCheckout);
  const defaultPlanId = useSelector(selectDefaultPlanId);
  const cardFormVisited = useSelector(selectIfCardFormShown);
  const couponDetails = useSelector(selectPlanCoupon);
  const stripeAccountName = useSelector(selectStripeAccountName);
  const paymentProvider = useSelector(selectPaymentProvider);
  const showSuccessPopup = useSelector(selectShowSuccessPopup);
  const isBraintree = paymentProvider === 'braintree';
  const isIntroOffer = landingType === 'intro_offer';
  const enablePaypal = process.env.REACT_APP_ENABLE_PAYPAL === 'true' && ['usd', 'eur'].includes(planDetails?.currency?.toLowerCase());
  const renderBrand = (card) => {
    if (!card) return null;
    const brand = CARD_BRANDS.find(
      (item) => item.name === card.name || item.name === card
    );
    if (!brand) return null;
    return (
      <img
        key={brand.name}
        src={`../assets/checkout/${brand.name}.png`}
        alt={brand.name}
        width={brand.width}
        height={brand.height}
      />
    );
  };

  useEffect(() => {
    if (plan) {
      setPlanDetails(PLANS[plan]);
    }
  }, [plan]);

  useEffect(() => {
    if (!isBraintree && stripePromise) {
      stripePromise.then((stripeObj) => {
        stripeInstance.current = stripeObj;
        const elements = stripeObj.elements();

        card.current = elements.create("cardNumber", {
          style: style,
          placeholder: "0000  0000  0000  0000",
        });
        expiration.current = elements.create("cardExpiry", {
          style: style,
          placeholder: t("MM/YY"),
        });
        cvc.current = elements.create("cardCvc", {
          style: style,
          placeholder: t("CVV"),
        });

        setLoad(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBraintree, stripePromise]);

  useLayoutEffect(() => {
    if (!isBraintree && stripeInstance.current && planDetails) {
      setPaymentRequest(null);
      const pr = stripeInstance.current.paymentRequest({
        country: "US",
        currency: planDetails?.currency?.toLowerCase() || "usd",
        total: {
          label: `Family Locator ${planDetails?.description} plan`,
          amount: Math.round((planDetails?.periodPrice - couponDetails?.couponPrice) * 100),
        },
        displayItems: [
          {
            label: `Family Locator ${planDetails?.description} plan`,
            amount: Math.round((planDetails?.periodPrice - couponDetails?.couponPrice) * 100),
          },
        ],
        requestPayerName: true,
        requestPayerEmail: true,
      });

      pr.canMakePayment().then((result) => {
        if (result && cardFormVisited && eventsData) {
          setPaymentRequest(pr);
        }
      });

      pr.on("paymentmethod", async (e) => {
        const successPayment = () => e.complete("success");
        const failedPayment = () => e.complete("fail");

        stripePurchaseClick(e, successPayment, failedPayment);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeInstance.current, planDetails, cardFormVisited, isBraintree, eventsData]);

  const initializeBraintree = useCallback((token) => {
    braintree.client.create(
      { authorization: token },
      (clientErr, clientInstance) => {
        if (clientErr) {
          console.error('Error creating client:', clientErr);
          return;
        }

        braintreeInstance.current = clientInstance;

        if (enablePaypal) {
          braintree.paypal.create(
            {client: clientInstance},
            (paypalErr, paypalInstance) => {
              if (paypalErr) {
                console.error('Error creating PayPal:', paypalErr);
                return;
              }

              paypal.current = paypalInstance;
            }
          );
        }
      }
    );
  }, [enablePaypal]);

  useEffect(() => {
    if (btToken && (isBraintree || enablePaypal)) {
      initializeBraintree(btToken);
    }
  }, [btToken, isBraintree, enablePaypal, initializeBraintree]);

  const onPayPalClick = () => {
    const localEventsData = { payment_method: 'paypal' };
    const extendedEventsData = {
      ...eventsData,
      ...localEventsData,
    };
    sendAnalyticsEvents(EVENTS.subBuyTapped, extendedEventsData);
    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, extendedEventsData);
    window?.ttq?.track('SubmitForm', {});

    paypal.current.tokenize(
      {
        flow: "vault",
        currency: planDetails?.currency || "USD",
      },
      function (tokenizeErr, payload) {
        if (tokenizeErr) {
          if (tokenizeErr.type !== "CUSTOMER") {
            console.error("Error tokenizing:", tokenizeErr);
          }
          dispatch(setCheckoutLoader({ show: false, type: "" }));
          return;
        }

        setAmplitudeUserProperties({ 'Payments gateways': 'Braintree' });
        dispatch(setEventDataList(localEventsData));
        dispatch(
          braintreePurchase(
            payload.nonce,
            null,
            null,
            plan,
            token,
            analyticsParams,
            extendedEventsData,
            couponDetails.couponName,
          )
        );
      }
    );
  };

  const onPurchaseClick = () => {
    if (isBraintree) {
      braintreePurchaseClick();
    } else {
      stripePurchaseClick(null);
    }
  };

  const stripePurchaseClick = (paymentMethod = null, successPayment = null, failedPayment = null) => {
    const paymentMethodName = paymentMethod?.walletName?.toLowerCase() || "card";
    const paymentCardBrandName = paymentMethod?.card?.brand || eventsData.cardBrand || "unknown";
    const paymentMethodObj = paymentMethod
      ? {
          methodName: paymentMethodName,
          stripeNonce: paymentMethod.paymentMethod.id,
        }
      : null;
    const localEventsData = {
      payment_method: paymentMethodName,
      cardBrand: paymentCardBrandName,
      card_brand: paymentCardBrandName,
      stripe_account_name: stripeAccountName,
    };
    const extendedEventsData = {
      ...eventsData,
      ...localEventsData,
    };

    window?.ttq?.track('SubmitForm', {});
    setAmplitudeUserProperties({ 'Payments gateways': `Stripe [${stripeAccountName}]` });
    dispatch(setEventDataList({ 'Payments gateways': `Stripe [${stripeAccountName}]` }));
    dispatch(setEventDataList(localEventsData));
    sendAnalyticsEvents(EVENTS.subBuyTapped, extendedEventsData);
    dispatch(
      sendMainPurchaseRequest(
        paymentMethodObj,
        stripeInstance.current,
        card.current,
        name,
        plan,
        token,
        analyticsParams,
        extendedEventsData,
        successPayment,
        failedPayment,
        couponDetails.couponName,
      )
    );
  };

  const braintreePurchaseClick = () => {
    const cardNumber = card.current.value;
    const cardExpiration = expiration.current.value;
    const cardCVV = cvc.current.value;

    const onError = (error) => {
      dispatch(setCheckoutLoader({ show: false, type: '' }));
      dispatch(setShowPaymentError({ show: true, text: error.message }));
    };

    const fullEventsData = {
      ...eventsData,
      payment_method: 'card',
    };
    sendAnalyticsEvents(EVENTS.subBuyTapped, fullEventsData);
    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, fullEventsData);
    window?.ttq?.track('SubmitForm', {});

    braintreeCardInstance.current.tokenize(
      {
        number: cardNumber,
        expirationDate: cardExpiration,
        cvv: cardCVV,
      },
      (error, payload) => {
        if (error) {
          console.error('Payment error', error);
          onError(error);
          return;
        }

        const cardBrand = payload?.details?.cardType || eventsData.cardBrand || 'unknown';
        const localEventsData = {
          payment_method: 'card',
          cardBrand,
          card_brand: cardBrand,
        };
        const extendedEventsData = {
          ...eventsData,
          ...localEventsData,
        };
        setAmplitudeUserProperties({ 'Payments gateways': 'Braintree' });
        dispatch(setEventDataList(localEventsData));
        dispatch(
          braintreePurchase(
            payload.nonce,
            payload.binData,
            braintreeThreeDSInstance.current,
            plan,
            token,
            analyticsParams,
            extendedEventsData,
            couponDetails.couponName,
          )
        );
      }
    );
  };

  const onLinkClick = (url) => window.open(url);

  useLayoutEffect(() => {
    if (showError) {
      navigate("/checkout/error");
    }
  }, [showError, navigate]);

  useEffect(() => {
    if (plan && planDetails && couponDetails && showCheckout) {
      const { periodPrice, currency } = planDetails;
      const { couponPrice } = couponDetails;
      const finalPrice = (periodPrice - couponPrice).toFixed(2);

      RedditPixel.track("AddToCart", {
        currency: currency || "USD",
        itemCount: 1,
        price: finalPrice,
      });
      window?.pintrk?.('track', 'addtocart', {
        value: finalPrice,
        order_quantity: 1,
        currency: currency || "USD",
      });
    }
  }, [plan, planDetails, couponDetails, showCheckout]);

  useEffect(() => {
    if (showCheckout && !showError) {
      dispatch(setIfCardFormShown(true));
      sendAnalyticsEvents(EVENTS.onboardingCheckoutShown, eventsData);
      window?.ttq?.track('InitiateCheckout', { content_id: plan });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCheckout, showError, dispatch]);

  useLayoutEffect(() => {
    if (isSuccess && isRunwayerFlow) {
      const { periodPrice, description, currency } = planDetails;
      sendRunwayerConversion({
        'onboardId': eventsData.utm.onboardId,
        'trialDays': 0,
        'planType': description,
        'planPrice': periodPrice - couponDetails?.couponPrice,
        'planCurrency': currency || 'USD',
      });
    }
  }, [planDetails, isSuccess, landingType, eventsData, couponDetails, isRunwayerFlow]);

  useLayoutEffect(() => {
    if (isSuccess) {
      const { periodPrice, productId, currency } = planDetails;
      const finalPrice = periodPrice - couponDetails?.couponPrice;

      sendPurchaseEvent(finalPrice, currency, plan, productId, subscriptionId, purchaseEventId, eventsData);
      dispatch(setShowSuccessPopup(true))
    }
  }, [isSuccess, planDetails, subscriptionId, plan, purchaseEventId, navigate, eventsData, couponDetails, dispatch]);

  const handleSuccessContinue = () => {
    sendAnalyticsEvents(`${EVENTS.popupEvent}__click`, {
      button__continue: 'continue',
      ...eventsData,
    });
    const paidWithPaypal = eventsData.payment_method === 'paypal';
    const postCheckoutPath = () => {
      if (paidWithPaypal) return 'success'; // must be first condition
      if (isRedirectToUpsell) return 'upsell';
      if (isHybridPlans) return 'extra';
      return 'success';
    };
    dispatch(setShowSuccessPopup(false));
    navigate(`/checkout/${postCheckoutPath()}`);
	};

  const separatorElement = (
    <PayWithContainer>
      <PayWithLine />
      <PayWithText>{t("or")}</PayWithText>
      <PayWithLine />
    </PayWithContainer>
  );

  const onBackClick = () => {
    sendAnalyticsEvents(EVENTS.subBuyClosed, eventsData);
    dispatch(setShowCheckout(false))
    dispatch(setCardholderName(''))
    dispatch(setPlan(defaultPlanId))
    clearFields();
  };

  const clearFields = () => {
    if (!(card?.current && expiration?.current && cvc?.current && cardholder?.current)) return;
    cardholder.current.value = '';
    if (!isBraintree) {
      const fieldsToClear = [card.current, expiration.current, cvc.current];
      fieldsToClear.forEach(field => field.clear());
    } else if (braintreeCardInstance.current) {
      ['number', 'expirationDate', 'cvv'].forEach(field => braintreeCardInstance.current.clear(field));
    }
  };

  const purchaseButton = (
    <AppButton
      onClick={() => onPurchaseClick(null)}
      disabled={!isFormValid}
      displayStubBtn={false}
      customId={`${isBraintree ? 'braintree' : 'stripe'}-continue-button`}
    />
  );

  const paymentCardMethod = (
		<>
      {isBraintree ? (
        <Braintree
          btToken={btToken}
          braintreeInstance={braintreeInstance.current}
          cardInstance={braintreeCardInstance}
          threeDSInstance={braintreeThreeDSInstance}
          card={card}
          expiration={expiration}
          cvc={cvc}
          cardholder={cardholder}
          planDetails={planDetails}
          separatorElement={separatorElement}
          isIntroOffer={isIntroOffer}
          renderBrand={renderBrand}
        >
          {purchaseButton}
        </Braintree>
      ) : (load && (
				<Stripe
					card={card.current}
					expiration={expiration.current}
					cvc={cvc.current}
					inputRef={cardholder}
					renderBrand={renderBrand}
				/>
			))}
      {!isBraintree && purchaseButton}
		</>
	);

	const paymentAppleGoogleMethods = (
		<>
			{(paymentRequest || paypal.current) && (
				<AlternativePaymentButtons>
					{paypal.current && (
						<>
							{!isIntroOffer && separatorElement}
              <PaypalButton id={'paypal-pay-button'} onClick={onPayPalClick} />
						</>
					)}
					{cardFormVisited && paymentRequest && (
						<>
							{!isIntroOffer && separatorElement}
							<Elements stripe={stripePromise}>
								<PaymentRequestContainer $mt={isIntroOffer && 24}>
									<PaymentRequestButtonElement
										options={{
											paymentRequest,
											id: 'apple-google-pay-button',
											style: {
												paymentRequestButton: {
													height: '56px',
													theme: 'dark',
													type: 'default',
												},
											},
										}}
									/>
								</PaymentRequestContainer>
							</Elements>
						</>
					)}
				</AlternativePaymentButtons>
			)}
		</>
	);

  const checkoutHeader = (
    <>
      <CheckoutHeadWrapper>
        <StyledBackButton onClick={onBackClick} id="come-back-button">
            {ArrowLeftIcon}
        </StyledBackButton>
        <Title>{t('checkout')}</Title>
      </CheckoutHeadWrapper>
    </>
  )

  return (
		<CheckoutWrapper>
			{checkoutHeader}
			{isHybridPlans && (
				<>
					<HybridPlan />
					<MoneyBackWrapper>
						<MoneyBackIcon />
						<MoneyBackTitle>
							{t('money_back_guarantee_checkout')}
						</MoneyBackTitle>
						<MoneyBackDescription
							dangerouslySetInnerHTML={{
								__html: t('money_back_description_checkout'),
							}}
						/>
					</MoneyBackWrapper>
				</>
			)}
			{isIntroOffer && (
				<>
					{paymentAppleGoogleMethods}
					{!isBraintree && (
						<CardIconsList>{CARD_BRANDS.map(renderBrand)}</CardIconsList>
					)}
				</>
			)}
			{paymentCardMethod}
			{!isIntroOffer && paymentAppleGoogleMethods}
			<MoneyBack>{t('money_back')}</MoneyBack>
			<StyledBtnWrapper>
				<StyledBtn
					id="link-to-terms"
					onClick={() =>
						onLinkClick('https://family-locator.com/terms-of-use-info/')
					}
				>
					{t('terms')}
				</StyledBtn>
				<StyledBtn
					id="link-to-privacy"
					onClick={() =>
						onLinkClick('https://family-locator.com/privacy-policy-info/')
					}
				>
					{t('privacy')}
				</StyledBtn>
			</StyledBtnWrapper>
			{isStage && (
				<input
					type="hidden"
					id="test-skip-checkout"
					onClick={() => navigate('/checkout/success')}
				/>
			)}
      {showSuccessPopup && <SuccessPopup isVisible={showSuccessPopup} handleSuccessContinue={handleSuccessContinue} />}
		</CheckoutWrapper>
	);
};

export default CheckoutScreen;
